import {
    datekeys,
    defaultScoreThresholds,
    durationkeys,
    meetingTypes,
} from "@callveriq/config/default.config";
import {
    IParameterState,
    ParameterStateType,
} from "@callveriq/modules/settings/modules/auditManager/components/context/ParameterStateContext";
import {
    AIScore,
    AuditStatus,
    ManualScore,
    Question,
    TemplateWithCategories,
} from "@callveriq/type/Audit";
import { MeetingTypeConst, NumericRange } from "@callveriq/type/Common";
import { UserType } from "@callveriq/type/User";
import { Team, SubTeam } from "@callveriq/type/Team";
import { EntityListResponse } from "@callveriq/type/Filter";
import {
    CIFiltersPayload,
    InsightDetail,
    InsightCountStats,
} from "@callveriq/type/customerIntelligence";
import domtoimage from "dom-to-image";
import {
    ConversationDetails,
    ConversationSearchDecodedFilters,
    SentimentTranscript,
    Transcript,
} from "@callveriq/type/Conversation";
import { FieldType, SearchFilter } from "@callveriq/type/Search";
import { NotificationSettings } from "@callveriq/config/dashboard.config";
import isEqual from "fast-deep-equal";
import {
    AuditType,
    ConversationScoreType,
    insightsFilterType,
    Level,
    moduleOptionsType,
} from "@callveriq/type/Filters.model";
import { DownloadReport } from "@callveriq/type/Report";
import { AnyAction, ThunkDispatch, nanoid } from "@reduxjs/toolkit";
import { showToast } from "../toast";
import { analyticsDashboardApiSlice } from "@callveriq/redux/services/home/analyticsDashboard.service";
import { CreateUpdateToastSettings } from "@callveriq/config/toast.config";
import { QmsField } from "@callveriq/modules/settings/modules/qmsManager/context/QmsStateContext";
import {
    MomentBlockFormType,
    MultiResponseBlockData,
    MultipleResponseNode,
    RuleEngineConfig,
} from "@callveriq/modules/settings/modules/auditManager/components/types/bert";
import { Node as ReactFlowNode } from "reactflow";
import { BertNodeKeys } from "@callveriq/modules/settings/modules/auditManager/components/bertRuleEngine/constants";
import { apiConfigs } from "@apis/axiosInstance";
import { IConversationDetails } from "@callveriq/modules/conversationDetails/context/ConversationDetailsProvider";
import { SxProps, Theme } from "@mui/material";
import CryptoJS from "crypto-js";
import { formatFloat } from "@callveriq/utils/helper";
import { MultiResponseNodeType } from "@callveriq/modules/settings/modules/auditManager/components/bertRuleEngine/context/NodeStateContext";
import { StatsThreshold } from "@callveriq/type/Domain";
import DomainInstance from "./DomainInstance";
import { getCustomDurationFilter } from "./search.helper";
import {
    GlobalFiltersInitialState,
    GlobalFiltersStateType,
} from "@callveriq/contexts/GlobalFiltersStateContextProvider";

// Add type definition for currency symbols
interface CurrencySymbols {
    INR: string;
    USD: string;
    [key: string]: string; // This is the index signature
}

export const billingConfig: CurrencySymbols = {
    INR: "₹",
    USD: "$",
};

function pad(d: number) {
    return d < 10 ? "0" + d.toString() : d.toString();
}

export const getUserName = (
    user:
        | UserType
        | Pick<UserType, "id" | "first_name" | "last_name" | "email">
        | null
        | undefined
): string => {
    if (user === null || user === undefined) return "";
    const { first_name, last_name, email, id } = user;
    return first_name && last_name
        ? `${first_name} ${last_name}`
        : first_name || email || id?.toString() || "-";
};

export const getQuestionType = (
    type: Question["question_type"]
): "Yes/No" | "Scale 0-10" | "Custom" =>
    type === "yes_no" ? "Yes/No" : type === "rating" ? "Scale 0-10" : "Custom";
export const colorsPalette = [
    "#6622CC",
    "#C83E4D",
    "#F8C537",
    "#F564A9",
    "#F98948",
    "#9B8816",
    "#AF1B3F",
    "#E27396",
    "#FF6B35",
    "#EC4E20",
    "#E34A6F",
    "#028090",
    "#FE5F55",
    "#F9E900",
    "#7F2982",
    "#C792DF",
    "#DC0073",
    "#FF312E",
    "#89FC00",
    "#00BD9D",
    "#EB5160",
    "#C287E8",
    "#FCBA04",
    "#613DC1",
    "#FF66B3",
    "#4E148C",
    "#63C132",
    "#54428E",
    "#7D8CC4",
    "#F5B700",
    "#3F84E5",
    "#008BF8",
    "#26D1F6",
    "#F7DD72",
    "#4D5DEE",
    "#3A70FD",
    "#1A8FE3",
];
export const aiInsightsColorPalette = [
    "#3A70FD",
    "#E34A6F",
    "#FE654F",
    "#63C132",
    "#6622CC",
    "#C83E4D",
    "#00BD9D",
    "#9B8816",
    "#3F84E5",
    "#7D8CC4",
    "#DC0073",
    "#7F2982",
];
export const getColorFromString = (
    name = "",
    { isAiInsight = false }: { isAiInsight?: boolean } = {}
): string => {
    function getRandomInteger(arrayLength: number): number {
        let integer = 0;
        for (let i = 0; i < name?.length; i += 1) {
            integer += name.charCodeAt(i);
        }
        return (
            // eslint-disable-next-line no-unsafe-optional-chaining
            (integer * name?.charCodeAt(0) || Math.random() * 100) % arrayLength
        );
    }
    const selectedColors = isAiInsight ? aiInsightsColorPalette : colorsPalette;

    return selectedColors[getRandomInteger(selectedColors.length)] || "#FF7D51";
};

export function capitalizeFirstLetter(val: string | null = ""): string {
    if (!isDefined(val)) return "";
    const str = val?.toLowerCase();
    return str?.charAt(0)?.toUpperCase() + str.slice(1);
}

export function isDefined<T>(value: T | undefined | null): value is T {
    return <T>value !== undefined && <T>value !== null;
}

export function getQuestionTypeLabel(
    value: Question["question_type"] | undefined
): string {
    switch (value) {
        case "yes_no":
            return "Yes or No";
        case "rating":
            return "Scale 0-10";
        case "custom":
            return "Custom";
        default:
            return "None";
    }
}

export const validateEmail = (email: string) => {
    return String(email)
        .toLowerCase()
        .match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        );
};

export const formatUnderScoreToSpace = (str: string) => {
    return str.replace(/_/g, " ");
};

export const downloadImage = (blob: string, fileName: string): void => {
    const fakeLink = window.document.createElement("a");
    fakeLink.style.display = "none";
    fakeLink.download = fileName;

    fakeLink.href = blob;

    document.body.appendChild(fakeLink);
    fakeLink.click();
    document.body.removeChild(fakeLink);

    fakeLink.remove();
};
export const getTimeZone = (): string => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export function dataURIToBlob(dataURI: string): Blob {
    dataURI = dataURI.replace(/^data:/, "");

    const mimeString = dataURI.split(",")[0].split(":")[1].split(";")[0];
    const base64 = dataURI.replace(/^[^,]+,/, "");
    const arrayBuffer = new ArrayBuffer(base64.length);
    const typedArray = new Uint8Array(arrayBuffer);

    for (let i = 0; i < base64.length; i++) {
        typedArray[i] = base64.charCodeAt(i);
    }

    return new Blob([arrayBuffer], { type: mimeString });
}

export const getDuration = function (start: string, end: string) {
    // get total seconds between the times
    let delta =
        Math.abs(new Date(end).getTime() - new Date(start).getTime()) / 1000;

    // calculate (and subtract) whole days
    const days = Math.floor(delta / 86400);
    delta -= days * 86400;

    // calculate (and subtract) whole hours
    const hours = Math.floor(delta / 3600) % 24;
    delta -= hours * 3600;

    // calculate (and subtract) whole minutes
    const minutes = Math.floor(delta / 60) % 60;
    delta -= minutes * 60;

    // what's left is seconds
    const seconds = Math.round(delta % 60);

    if (days > 0) {
        return `${pad(days)}d, ${hours}:${pad(minutes)}:${pad(seconds)}`;
    }
    if (hours) {
        return `${pad(hours)}:${pad(minutes)}:${pad(Math.ceil(seconds))}`;
    }
    return `${pad(minutes)}:${pad(Math.ceil(seconds))}`;
};

export const getFileType = (
    fileName: string | null
): "audio" | "video" | "document" | "transcription" => {
    if (fileName === null) return "document";
    const audio = {
        mp3: "mp3",
        ogg: "ogg",
        amr: "amr",
        au: "au",
        awb: "awb",
        flac: "flac",
        mid: "mid",
        wav: "wav",
        wma: "wma",
        mka: "mka",
        acc: "acc",
        ac3: "ac3",
        aiff: "aiff",
        smf: "smf",
        aac: "aac",
        "amr-wb": "amr-wb",
        "x-amr-wb": "x-amr-wb",
        m4a: "m4a",
        "3gpp": "3gpp",
    };
    const video = {
        mp4: "mp4",
        wav: "wav",
        mov: "mov",
        wmv: "wmv",
        avi: "avi",
        flv: "flv",
        mpg: "mpg",
        mpeg: "mpeg",
        avchd: "avchd",
        f4v: "f4v",
        swf: "swf",
        mkv: "mkv",
        webm: "webm",
        m4a: "m4a",
        "3gp": "3gp",
    };

    const transcription = {
        Transcript: "Transcript",
    };

    const extension = fileName?.split?.(".")?.at?.(-1) as
        | keyof typeof audio
        | keyof typeof video
        | keyof typeof transcription
        | undefined;

    if (!extension) return "document";

    if (extension in audio) return "audio";
    else if (extension in video) return "video";
    else if (extension in transcription) return "transcription";
    return "document";
};

export const downloadFileFromUrl = (fileName: string, url: string) => {
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

export const getInsightValue = (
    insights: insightsFilterType["insights"]
): string | number | null => {
    return insights.level3 ?? insights.level2 ?? insights.level1 ?? null;
};

export function replaceYouTubeWatchWithEmbed(url: string): string {
    // Regular expression to match YouTube watch URLs
    const watchRegex = /https:\/\/www\.youtube\.com\/watch\?v=/;

    // Check if the URL matches the watchRegex
    if (watchRegex.test(url)) {
        // Replace "watch" with "embed"
        const embedUrl = url.replace(
            watchRegex,
            "https://www.youtube.com/embed/"
        );
        return embedUrl;
    }

    // If the URL is not a YouTube watch URL, return the original URL
    return url;
}

export function getFileNameAndExtension(filePath: string): {
    fileName: string;
    fileExtension?: string;
} {
    // Get the last portion of the path after the last '/'
    const fileNameWithExtension: string = filePath.substring(
        filePath.lastIndexOf("/") + 1
    );

    // Check if there is a dot in the file name
    const dotIndex: number = fileNameWithExtension.lastIndexOf(".");

    if (dotIndex !== -1) {
        // Get the file name without extension
        const fileName: string = fileNameWithExtension.substring(0, dotIndex);

        // Get the file extension
        const fileExtension: string = fileNameWithExtension.substring(
            dotIndex + 1
        );

        return { fileName, fileExtension };
    } else {
        // If there is no dot in the file name, assume there is no extension
        return { fileName: fileNameWithExtension, fileExtension: undefined };
    }
}
export const merge = <T>(args: T[][]): T[] =>
    args.reduce((prev, next) => prev.concat(next), [] as T[]);

export const formatFloatNumber = (num = 0, fixed = 0) => {
    if (isNaN(num) || !isFinite(num)) return 0;
    const regexPattern = /^-?[0-9]+$/;
    // check if the passed number is integer or float

    if (regexPattern.test(num.toString())) {
        return num;
    } else {
        if (typeof num === "number") return Number(num.toFixed(fixed));
    }

    return num;

    // check if the passed number is integer or float
};

export function paginator<T>(
    items: T[] | undefined = [],
    current_page: number,
    per_page_items: number
) {
    const page = current_page || 1,
        per_page = per_page_items,
        offset = (page - 1) * per_page,
        paginatedItems = items.slice(offset).slice(0, per_page_items),
        total_pages = Math.ceil(items.length / per_page);

    return {
        page: page,
        per_page: per_page,
        pre_page: page - 1 ? page - 1 : null,
        next_page: total_pages > page ? page + 1 : null,
        total: items.length,
        total_pages: total_pages,
        data: paginatedItems,
    };
}

export const flattenTeams = (teams: Team[]): Array<Team | SubTeam> => {
    let temp: Array<Team | SubTeam> = [];

    for (let i = 0; i < teams.length; i++) {
        const e = teams[i];
        if (e?.subteams?.length) {
            temp = [...temp, ...e.subteams];
        } else {
            temp = [...temp, e];
        }
    }
    return temp;
};

export function numFormatter(num: number): number | string {
    num = Number(num);

    if (num > 999 && num < 1000000) {
        return (Math.floor(num / 100) / 10).toFixed(1) + "K"; //Math.floor(num / 100) / 10: This effectively removes all digits after the first decimal place without rounding, as it truncates the value.
    } else if (num > 1000000) {
        return (Math.floor(num / 100000) / 10).toFixed(1) + "M";
    }
    return num; // if value < 1000, nothing to do
}

export function ciPayloadWithPrimary(
    payload: CIFiltersPayload,
    is_primary: boolean,
    insightId?: number
): CIFiltersPayload {
    return {
        ...payload,
        is_primary: is_primary || undefined,
        insight_id: insightId,
    };
}

export const flattenInsights = (
    insights: InsightDetail[],
    parentId: number | null = null
): InsightDetail[] => {
    return insights.flatMap((insight) => [
        { ...insight, parentId },
        ...flattenInsights(insight.child_insights, insight.id),
    ]);
};

export const getInsightPercent = ({
    stats,
    count,
    calculationType,
    type,
    summary,
}: InsightCountStats) => {
    if (!stats) return formatFloat(0, 1);
    const isAnalysedInsights = "total_meetings" in stats;
    const total =
        calculationType === "calls"
            ? type === "sentiment"
                ? summary?.total_calls
                : isAnalysedInsights
                ? stats?.total_meetings
                : stats?.analyzed_calls
            : type === "sentiment"
            ? summary?.total_accounts
            : stats?.total_accounts;
    return formatFloat(total ? (count / total) * 100 : 0, 1);
};

export const getDurationInSeconds = (
    start: string | Date = new Date(),
    end: string | Date = new Date()
): number => {
    return Math.abs(new Date(end).getTime() - new Date(start).getTime()) / 1000;
};

export const omitKeys = <
    T extends Record<string | number | symbol, unknown>,
    U extends Record<string, "">,
    V = Omit<T, keyof U>
>(
    obj: T,
    excludeObj: U
): V => {
    const excludeKeys = Object.keys(excludeObj);
    const returnObj = { ...obj };
    Object.keys(obj).forEach((key) => {
        if (excludeKeys.includes(key)) {
            delete returnObj[key];
        }
    });
    return returnObj as unknown as V;
};

export const appendLinkTags = (url: string): string => {
    const urlPattern =
        /(?:https?:\/\/|www\.)[\w-]+(\.[\w-]+)+[\w.,@?^=%&:/~+#-]*[\w@?^=%&/~+#-]/g;

    return url.replace(urlPattern, (match) => {
        const prefixedMatch = match.startsWith("http")
            ? match
            : `https://${match}`;
        return `<a href="${prefixedMatch}" target="_blank">${match}</a>`;
    });
};

export const getDomainBaseLink = (): string => {
    const protocol = import.meta.env.VITE_APP_API_PROTOCOL || apiConfigs.HTTPS;
    const { hostname } = new URL(window.location.href);
    return !import.meta.env.PROD
        ? `http://${hostname}:3000`
        : `${protocol}${hostname}`;
};

export const getDomain = (): string => {
    const { hostname } = new URL(window.location.href);
    return hostname.split(".")?.[0] ?? "";
};

export const getBaseUrl = (): string => {
    const { hostname } = new URL(window.location.href);
    return import.meta.env.PROD ? hostname.split(".")?.[1] : "localhost:3000";
};

export async function blobToObject(blob: Blob): Promise<unknown> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = function () {
            try {
                const object = JSON.parse(reader.result as string);
                resolve(object);
            } catch (error) {
                reject(error);
            }
        };

        reader.onerror = function (error) {
            reject(error);
        };

        reader.readAsText(blob);
    });
}

export function getAuditConfigType(
    value: IParameterState["ai_audit_model"],
    branding?: string
): string {
    switch (value) {
        case "expert":
            return "Expert";
        case "advanced":
            return "Rule Engine";
        case "na":
            return "Not Applicable";
        case "basic":
            return `${branding || "Callveriq"} LLM`;
    }

    return "";
}

export function grpcPayloadConstructor(
    payload: Record<string, unknown>,
    {
        beginsWithKeys,
    }: {
        beginsWithKeys: Array<string>;
    } = {
        beginsWithKeys: [],
    }
): string {
    return Object.keys(payload)
        .reduce((acc, key) => {
            if (!isDefined(payload[key])) {
                return acc + "";
            }
            if (Array.isArray(payload[key])) {
                if ((payload[key] as Array<unknown>).length) {
                    return (
                        acc +
                        `${key}=in:${(payload[key] as Array<unknown>).join(
                            ","
                        )};`
                    );
                } else {
                    return acc + "";
                }
            }

            if (typeof payload[key] === "object") {
            }

            if (payload[key] === true) {
                return acc + `${key};`;
            }
            if (
                key === "overall_sentiment" ||
                key === "gpt_overall_sentiment"
            ) {
                return acc + `${key}=${payload[key]};`;
            }
            if (beginsWithKeys.includes(key)) {
                return acc + `${key}=begins_with:${payload[key]};`;
            }

            return acc + `${key}=eq:${payload[key]};`;
        }, "")
        .trim()
        .replace(/\s+/gm, "");
}

export function newGrpcPayloadConstructor(
    payload: Record<
        string,
        {
            gte?: number | string;
            lte?: number | string;
            eq?: number | string | null;
            begins_with?: string;
            in?: number[] | string[];
        }
    >
): string {
    return Object.keys(payload)
        .reduce((acc, key) => {
            if (isDefined(payload[key])) {
                const fieldObj = payload[key];
                const fieldEntries = Object.entries(fieldObj);

                // Process all field entries
                const fieldStrings = fieldEntries
                    .map(([fieldType, fieldValue]) => {
                        if (
                            !isDefined(fieldValue) ||
                            (Array.isArray(fieldValue) &&
                                fieldValue.length === 0)
                        ) {
                            return "";
                        }
                        return `${key}=${fieldType}:${
                            Array.isArray(fieldValue)
                                ? fieldValue.join(",")
                                : fieldValue
                        };`;
                    })
                    .filter((str) => str !== "");

                return acc + fieldStrings.join("");
            }
            return acc + "";
        }, "")
        .trim()
        .replace(/\s+/gm, "");
}

export function convertSecondsToTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = Math.floor(seconds % 60);

    return `${hours > 9 ? hours : `0${hours}`}:${
        minutes > 9 ? minutes : `0${minutes}`
    }:${
        remainingSeconds > 9
            ? remainingSeconds.toFixed()
            : `0${remainingSeconds.toFixed()}`
    }`;
}

export default function invariant(
    cond?: boolean,
    message?: string
): asserts cond {
    if (cond) {
        return;
    }

    throw new Error(
        "Internal Lexical error: invariant() is meant to be replaced at compile " +
            "time. There is no runtime version. Error: " +
            message
    );
}

export function replaceKeyInObjectsArray(
    objects: Array<Record<string, unknown>>,
    oldKey: string,
    newKey: string
): Array<Record<string, unknown>> {
    return objects.map((obj) => {
        if (oldKey in obj) {
            obj[newKey] = obj[oldKey];
            delete obj[oldKey];
        }
        return obj;
    });
}

export function replacePlaceholders(
    template: string,
    values: Record<string, string>
): string {
    return template.replace(
        /{{(.*?)}}/g,
        (match, key) => values[key.trim()] || match
    );
}

export const getMeetingTypeLabel = (value: MeetingTypeConst) => {
    return meetingTypes.find((type) => type.value === value)?.name || "";
};

export const objectKeyMap = (
    data: SearchFilter[]
): Record<FieldType, SearchFilter | never> => {
    const keyMapData = {} as Record<FieldType, SearchFilter | never>;
    for (const searchFilter of data) {
        keyMapData[searchFilter["name"]] = searchFilter;
    }
    return keyMapData;
};

export const exportAsImage = async (
    element: HTMLElement,
    imageFileName: string,
    backgroundColor: string = "white"
) => {
    const scale = 2;

    const style = {
        transform: `scale(${scale})`,
        transformOrigin: "top left",
        width: element.offsetWidth + "px",
        height: element.offsetHeight + "px",
    };

    const param = {
        height: element.offsetHeight * scale,
        width: element.offsetWidth * scale,
        quality: 1,
        bgcolor: backgroundColor,
        style,
    };

    domtoimage
        .toPng(element, param)
        .then(function (dataUrl: string) {
            downloadImage(dataUrl, imageFileName);
        })
        .catch(function (error: Error) {
            console.error("oops, something went wrong!", error);
        });
};

export const replaceSpaceWithHyphen = ({
    str,
    convertToSmallCase = false,
}: {
    str: string;
    convertToSmallCase?: boolean;
}) => {
    if (convertToSmallCase) {
        return str.replace(/\s+/g, "-").toLowerCase();
    } else {
        return str.replace(/\s+/g, "-");
    }
};
export const objectKeyArrayMap = (
    data: SearchFilter[]
): Record<FieldType, Array<SearchFilter | never>> => {
    const keyMapData = {} as Record<FieldType, Array<SearchFilter | never>>;
    for (const searchFilter of data) {
        keyMapData[searchFilter["name"]] = [
            ...(keyMapData[searchFilter["name"]] ?? []),
            searchFilter,
        ];
    }
    return keyMapData;
};

export function mergeContinuousSpeakers(
    speakers: Transcript[] = []
): Transcript[] {
    const mergedSpeakers: Transcript[] = [];
    try {
        for (const speaker of speakers) {
            let lastSpeaker = mergedSpeakers[mergedSpeakers.length - 1];

            // Check if the current speaker is the same as the last one in the merged array
            const isSameSpeaker =
                lastSpeaker &&
                (!isDefined(lastSpeaker.speaker_id) ||
                !isDefined(speaker.speaker_id)
                    ? lastSpeaker.speaker_name === speaker.speaker_name
                    : lastSpeaker.speaker_id === speaker.speaker_id);
            const isContinuous =
                lastSpeaker &&
                Math.abs(lastSpeaker.end_time - speaker.start_time) < 0.5;

            if (isSameSpeaker && isContinuous) {
                // Create a new object combining the last speaker and current speaker
                lastSpeaker = {
                    ...lastSpeaker,
                    end_time: speaker.end_time,
                    monologue_text:
                        lastSpeaker.monologue_text +
                        " " +
                        speaker.monologue_text,
                    // Merge other properties as needed
                };

                // For word alignment, handle it separately if defined
                if (
                    isDefined(lastSpeaker?.word_alignment) &&
                    isDefined(speaker?.word_alignment)
                ) {
                    lastSpeaker.word_alignment = [
                        ...lastSpeaker.word_alignment,
                        ...speaker.word_alignment,
                    ];
                }

                // Replace the last speaker with the updated one
                mergedSpeakers[mergedSpeakers.length - 1] = lastSpeaker;
            } else {
                // Add the current speaker as a new entry
                mergedSpeakers.push({ ...speaker });
            }
        }
    } catch (err) {
        console.error(err);
    }

    return mergedSpeakers;
}
export function generateTopicsFromTranscript(
    speakers: Transcript[] = []
): IConversationDetails["topics"] {
    const topicObj: IConversationDetails["topics"] = {};
    for (const {
        topics,
        start_time,
        end_time,
        monologue_text,
        speaker_type,
        speaker_name,
    } of speakers) {
        if (Object.keys(topics).length) {
            for (const topic of Object.keys(topics)) {
                const color = getColorFromString(topic);
                if (!topicObj[topic]) {
                    topicObj[topic] = {
                        color,
                        phrases: {},
                        transcript: [
                            {
                                start_time,
                                end_time,
                                monologue_text,
                                color,
                                speaker_type,
                                speaker_name,
                            },
                        ],
                    };
                } else {
                    topicObj[topic] = {
                        ...topicObj[topic],
                        transcript: [
                            ...topicObj[topic].transcript,
                            {
                                start_time,
                                end_time,
                                monologue_text,
                                color,
                                speaker_type,
                                speaker_name,
                            },
                        ],
                    };
                }
                const phrases = topics[topic].map((e) => e.detected_phrases);

                for (const phrase of phrases) {
                    for (const uniquePhrase of Object.keys(phrase)) {
                        if (!isDefined(topicObj[topic].phrases[uniquePhrase]))
                            topicObj[topic].phrases[uniquePhrase] = {
                                count: 1,
                                transcript: [
                                    {
                                        start_time,
                                        end_time,
                                        monologue_text: phrase[uniquePhrase],
                                        speaker_type,
                                        speaker_name,
                                    },
                                ],
                            };
                        else {
                            topicObj[topic].phrases[uniquePhrase] = {
                                count:
                                    topicObj[topic].phrases[uniquePhrase]
                                        .count + 1,
                                transcript: [
                                    ...topicObj[topic].phrases[uniquePhrase]
                                        .transcript,
                                    {
                                        start_time,
                                        end_time,
                                        monologue_text: phrase[uniquePhrase],
                                        speaker_type,
                                        speaker_name,
                                    },
                                ],
                            };
                        }
                    }
                }
            }
        }
    }

    return topicObj;
}

export function formatTranscriptBasedOnSpeakers(
    speakers: Transcript[] = []
): Record<string, Transcript[]> {
    const obj: Record<string, Transcript[]> = {};
    speakers?.forEach((speaker) => {
        if (!isDefined(obj[speaker.speaker_id])) {
            obj[speaker.speaker_id] = [];
        }
        obj[speaker.speaker_id].push(speaker);
    });
    return obj;
}

export function getQuestionsAndActionItemsFromTranscript(
    transcripts: Transcript[] = []
): Pick<IConversationDetails, "questions" | "actionItems"> {
    const questions: IConversationDetails["questions"] =
        {} as IConversationDetails["questions"];
    const actionItems: IConversationDetails["actionItems"] =
        {} as IConversationDetails["questions"];
    for (const {
        sentence_categories,
        speaker_name,
        speaker_type,
        speaker_id,
    } of transcripts) {
        const question = sentence_categories["question"];
        if (sentence_categories["question"]?.length) {
            if (!questions[speaker_id]) {
                questions[speaker_id] = {
                    speaker_id,
                    speaker_name,
                    speaker_type,
                    transcripts: question.map((q) => ({
                        ...q,
                        speaker_type,
                        speaker_id,
                        speaker_name,
                    })),
                };
            } else {
                questions[speaker_id] = {
                    ...questions[speaker_id],
                    transcripts: [
                        ...questions[speaker_id].transcripts,
                        ...question.map((q) => ({
                            ...q,
                            speaker_type,
                            speaker_id,
                            speaker_name,
                        })),
                    ],
                };
            }
        }
        const action = sentence_categories["action"];
        if (action?.length) {
            if (!actionItems[speaker_id]) {
                actionItems[speaker_id] = {
                    speaker_id,
                    speaker_name,
                    speaker_type,
                    transcripts: action.map((a) => ({
                        ...a,
                        speaker_type,
                        speaker_id,
                        speaker_name,
                    })),
                };
            } else {
                actionItems[speaker_id] = {
                    ...actionItems[speaker_id],
                    transcripts: [
                        ...actionItems[speaker_id].transcripts,
                        ...action.map((a) => ({
                            ...a,
                            speaker_type,
                            speaker_id,
                            speaker_name,
                        })),
                    ],
                };
            }
        }
    }

    return {
        questions,
        actionItems,
    };
}

export function getConversationSpeakerInfo(
    speakers: Transcript[] = []
): Record<string, { name: string; speakerType: string }> {
    const obj: Record<string, { name: string; speakerType: string }> = {};
    speakers?.forEach((speaker) => {
        if (!isDefined(obj[speaker.speaker_id])) {
            obj[speaker.speaker_id] = {
                name: speaker.speaker_name,
                speakerType: speaker.speaker_type,
            };
        }
    });
    return obj;
}

export const generateSentimentMonologue = (
    snippets: ConversationDetails["stats"]["sentiment_snippets"] | undefined
) => {
    if (!isDefined(snippets)) {
        return {};
    }

    const { negative = [], positive = [] } = snippets;

    const sentimentMonologues = {} as Record<
        string,
        Array<SentimentTranscript>
    >;
    for (const transcript of [
        ...negative.map((e) => ({ ...e, sentiment_score: -1 })),
        ...positive.map((e) => ({ ...e, sentiment_score: 1 })),
    ]) {
        if (!isDefined(sentimentMonologues?.[transcript.speaker_name])) {
            sentimentMonologues[transcript.speaker_name] = [];
        }
        sentimentMonologues[transcript?.speaker_name]?.push({
            start_time: transcript.start_time,
            end_time: transcript.end_time,
            speaker_name: transcript.speaker_name,
            speaker_type: transcript.speaker_type,
            sentiment:
                transcript?.sentiment_score > 0 ? "positive" : "negative",
        });
    }

    return sentimentMonologues;
};

export function stringToColor(string: string) {
    let hash = 0;
    let i;

    /* eslint-disable no-bitwise */
    for (i = 0; i < string.length; i += 1) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = "#";

    for (i = 0; i < 3; i += 1) {
        const value = (hash >> (i * 8)) & 0xff;
        color += `00${value.toString(16)}`.slice(-2);
    }
    /* eslint-enable no-bitwise */
    return color;
}

export function stringAvatar(name: string, sx?: SxProps<Theme>) {
    return {
        sx: {
            bgcolor: stringToColor(name),
            ...sx,
        },
        children: `${name.split(" ")?.[0]?.[0]}`,
    };
}

const flattenDataInObject = (
    data: null | Record<string, unknown>,
    newObj: Record<string, unknown>
) => {
    if (!isDefined(data)) return {};
    if (typeof data == "object" && Object.keys(data).length) {
        Object.keys(data).forEach((e) => {
            if (typeof data[e] != "object") {
                newObj[e] = data[e];
            }
            if (
                Array.isArray(data[e]) &&
                (typeof (data[e] as Array<string>)?.[0] == "string" ||
                    typeof (data[e] as Array<number>)?.[0] == "number")
            ) {
                newObj[e] = (data[e] as Array<string | number>).join(", ");
            }
            if (
                typeof data[e] == "object" &&
                !(
                    typeof (data[e] as Array<string>)?.[0] == "string" ||
                    typeof (data[e] as Array<string>)?.[0] == "number"
                )
            ) {
                flattenDataInObject(data[e] as Record<string, unknown>, newObj);
            }
        });
    }
};

export const flattenObject = (
    data: Record<string, unknown>
): Record<string, string> => {
    const myObj = {};
    flattenDataInObject(data, myObj);
    return myObj;
};

export const getScore = (
    args: AuditStatus<ManualScore | AIScore>["scores"] | undefined | null
): [string | null, number | null] => {
    if (!isDefined(args)) return [null, null];
    const { template_marks_audited, template_score } = args;
    return [
        `${formatFloatNumber(template_score, 2)} / ${formatFloatNumber(
            template_marks_audited,
            1
        )}`,
        template_marks_audited !== 0
            ? formatFloatNumber(
                  (template_score / template_marks_audited) * 100,
                  1
              )
            : null,
    ];
};

export const getManualDraftScore = (
    args: AuditStatus<ManualScore>["scores"] | undefined | null
): [string | null, number | null] => {
    if (!isDefined(args)) return [null, null];
    const { draft_template_marks_audited, draft_template_score } = args;
    return [
        draft_template_marks_audited === 0
            ? null
            : `${formatFloatNumber(
                  draft_template_score,
                  1
              )}/${formatFloatNumber(draft_template_marks_audited, 1)}`,
        formatFloatNumber(
            (draft_template_score / draft_template_marks_audited) * 100,
            1
        ),
    ];
};

export const getParameterMaxWeight: (
    question: Pick<Question, "question_type" | "settings">
) => number | null = ({
    question_type,
    settings,
}: Pick<Question, "question_type" | "settings">) => {
    switch (question_type) {
        case "rating":
            return settings.weight;
        case "custom":
            return Math.max(...settings.custom.map((e) => e.weight));
        case "yes_no":
            return Math.max(settings.yes_weight || 0, settings.no_weight || 0);
        default:
            return null;
    }
};

export function splitText(text: string, query: string): string[] {
    if (!query) return [text];

    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${escapedQuery})`, "gi");
    return text.split(regex);
}

export function findImageUrl(text: string): string | null {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const imageUrlRegex = /\.(jpeg|jpg|gif|png|svg)$/i;

    const urls = text.match(urlRegex);
    if (urls) {
        for (const url of urls) {
            if (imageUrlRegex.test(url)) {
                return url;
            }
        }
    }
    return null;
}

export function scrollElementIntoView(
    selector: string,
    scrollOptions?: boolean | ScrollIntoViewOptions
): void {
    const element = document.querySelector(selector);
    if (element) {
        element.scrollIntoView(
            scrollOptions ?? {
                behavior: "smooth",
                block: "start",
                inline: "nearest",
            }
        );
    } else {
        console.error(`Element with selector '${selector}' not found.`);
    }
}

export function scrollDashboardIntoView(ref: HTMLDivElement): void {
    if (ref) {
        ref.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "nearest",
        });
    } else {
        console.error("Dashboard ref is not defined");
    }
}

export const groupByDate = <T extends Record<U, unknown>, U extends keyof T>(
    data: T[],
    dateKey: U
): Array<T & { firstofDay?: boolean }> => {
    let firstOfKey: string | null = null;
    return [...data].map((item) => {
        if (firstOfKey === new Date(item[dateKey] as string).toDateString()) {
            return item;
        } else {
            firstOfKey = new Date(item[dateKey] as string).toDateString();
            return { ...item, firstOfDay: true };
        }
    });
};

export const getNotificationId = (value: string): string => {
    if (!isDefined(value)) return "Immediately";
    for (const key in NotificationSettings) {
        if (isEqual(NotificationSettings[key], JSON.parse(value))) {
            return key;
        }
    }
    return "Immediately";
};

export const generateRandomNumber = ({
    min,
    max,
}: {
    min: number;
    max: number;
}) => Math.floor(Math.random() * (max - min + 1)) + min;

export const getMinMaxLabel = ({
    min,
    max,
    unit = "",
}: {
    min: number | null;
    max: number | null;
    unit?: string;
}): string => {
    return isDefined(min) && isDefined(max)
        ? `Between ${min} - ${max} ${unit}`
        : isDefined(min)
        ? `Above ${min} ${unit}`
        : isDefined(max)
        ? `Below ${max} ${unit}`
        : "";
};

export const extractTranscriptURL = (url?: string) => {
    if (!url) {
        return null;
    }
    return url;
};

export const conversationScoreMapping = (
    option: ConversationScoreType | null,
    thresholds: StatsThreshold | null
): NumericRange => {
    const scoreThresholds = thresholds ?? defaultScoreThresholds;

    switch (option) {
        case ConversationScoreType.good:
            return [scoreThresholds?.good, null];
        case ConversationScoreType.average:
            return [scoreThresholds?.average, scoreThresholds?.good];
        case ConversationScoreType.bad:
            return [null, scoreThresholds?.bad];
        default:
            return [null, null];
    }
};

export const getScoreNameFromScore = (
    score: number | null,
    thresholds: StatsThreshold | null
): ConversationScoreType | null => {
    const scoreThresholds = thresholds ?? defaultScoreThresholds;

    if (score === null) return null;

    if (score >= scoreThresholds.good) {
        return ConversationScoreType.good;
    } else if (score >= scoreThresholds.average) {
        return ConversationScoreType.average;
    } else if (score < scoreThresholds.bad) {
        return ConversationScoreType.bad;
    }
    return null;
};

export const getParameterResponseOptions = (
    e: Question | ParameterStateType | undefined
): Array<{ label: string; id: number | string }> => {
    if (!isDefined(e)) return [];
    if (e.question_type === "yes_no") {
        return [
            { id: 1, label: "Yes" },
            { id: 0, label: "No" },
            { id: -1, label: "NA" },
        ];
    }
    if (e.question_type === "rating") {
        return [
            ...new Array(11)
                .fill(0)
                .map((_, idx) => ({ id: idx, label: idx.toString() })),
            { id: -1, label: "NA" },
        ];
    }
    if (e.question_type === "custom") {
        return e.settings.custom.map((e) => ({ id: e.id, label: e.name }));
    }
    return [];
};
export const extractFileType = (mediaUrl: string): string => {
    const tempArr = mediaUrl.split("?")?.[0]?.split(".");
    if (tempArr) return tempArr[tempArr.length - 1];
    else return "";
};

export const checkFileType = (
    file_type: string
): "audio" | "video" | "document" => {
    if (file_type === null) return "video";
    const audio = {
        mp3: "mp3",
        ogg: "ogg",
        amr: "amr",
        au: "au",
        awb: "awb",
        flac: "flac",
        mid: "mid",
        wav: "wav",
        wma: "wma",
        mka: "mka",
        webm: "webm",
        acc: "acc",
        ac3: "ac3",
        aiff: "aiff",
        "3gpp": "3gpp",
        smf: "smf",
        aac: "aac",
        "amr-wb": "amr-wb",
        "x-amr-wb": "x-amr-wb",
        m4a: "m4a",
    };
    const video = {
        mp4: "mp4",
        wav: "wav",
        mov: "mov",
        wmv: "wmv",
        avi: "avi",
        flv: "flv",
        mpg: "mpg",
        mpeg: "mpeg",
        avchd: "avchd",
        f4v: "f4v",
        swf: "swf",
        mkv: "mkv",
        webm: "webm",
        m4a: "m4a",
        "3gp": "3gp",
    };
    if (file_type in audio) {
        return "audio";
    } else if (file_type in video) {
        return "video";
    } else return "document";
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const updateDownloadQue: (params: {
    data: DownloadReport;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatch: ThunkDispatch<any, any, AnyAction>;
    message?: string;
    type?: "internal";
}) => void = ({
    data,
    dispatch,
    message = "Your report is generating. You will be notified once it is ready.",
    type,
}) => {
    showToast({
        message,
        ...CreateUpdateToastSettings,
    });
    dispatch(
        analyticsDashboardApiSlice.util.updateQueryData(
            "getReportsDownloadQue",
            isDefined(type) ? { type } : {},
            (draft) => {
                return {
                    ...draft,
                    count: draft.count + 1,
                    results: [data, ...draft.results],
                };
            }
        )
    );
};

/**
 * @param string
 * @returns string
 */
export const cleanSourceHandle = (e: string): string => {
    if (e.includes("-bottom")) {
        return e.split("-bottom")[0];
    }
    if (e.includes("-right")) {
        return e.split("-right")[0];
    }
    return e;
};

export function getParameters(
    data: TemplateWithCategories | undefined
): Question[] {
    const allQuestions: Question[] = [];
    if (!isDefined(data)) {
        return allQuestions;
    }
    for (const category of data?.categories) {
        if (category.questions) {
            allQuestions.push(...category.questions);
        }
    }
    return allQuestions;
}
export const getQmsFieldsForBulkOpload = (
    e: QmsField[] | undefined
): Array<Pick<QmsField, "name" | "type" | "is_mandatory">> => {
    if (!isDefined(e)) return [];
    return [
        ...e.filter((e) => !e.is_disabled),
        {
            name: "Agent Team",
            type: "owner_team",
            is_mandatory: false,
        },
        {
            name: "Agent First Name",
            type: "owner_first_name",
            is_mandatory: false,
        },
        {
            name: "Agent Last Name",
            type: "owner_last_name",
            is_mandatory: false,
        },
        {
            name: "User Id",
            type: "owner_primary_phone",
            is_mandatory: false,
        },
    ];
};

/**
 * Generates a NanoID and replaces '-' characters with random digits.
 * @param {number} size - The size of the NanoID to generate.
 * @returns {string} - The generated NanoID with '-' replaced by random digits.
 */
export function generateCleanNanoID(size: number = 10): string {
    const id = nanoid(size);
    return id.replace(/_/g, () => Math.floor(Math.random() * 10).toString());
}

/**
 * Heals a group block's internal reactflow graph when the child nodes have
 * lost their ids (persisted/cloned with `id: ""`), which causes reactflow to
 * dedupe duplicate empty ids down to a single node and drop the edges that
 * still reference the original (now-lost) ids.
 *
 * Strategy:
 *  - Regenerate a fresh unique id for every node whenever any id is empty or
 *    duplicated (so all moment nodes survive).
 *  - Remap each edge to the new ids, preserving its `data`/operator (AND/OR):
 *      1. Direct: when an endpoint still references a valid, unique node id.
 *      2. Topology alignment: the stored edges still describe a consistent
 *         parent→child tree over the (now lost) old ids, so that tree is
 *         aligned onto the physical layout (child = parent offset right by 400
 *         or bottom by 200, matching `createSingleResponseNode`) by matching
 *         roots and walking both graphs in lock-step. This keeps each operator
 *         on its correct connection regardless of the stored edge order.
 *      3. Geometry fallback: for anything the alignment can't resolve, pair a
 *         source with the node sitting at its expected child offset.
 *    Unrecoverable edges are dropped.
 *
 * No-op (returns the same arrays) when every node already has a unique,
 * non-empty id.
 */
export function healGroupBlockInternalGraph<
    N extends {
        id: string;
        type?: string;
        position?: { x: number; y: number };
        positionAbsolute?: { x: number; y: number };
    },
    E extends {
        id?: string;
        source: string;
        target: string;
        sourceHandle?: string | null;
        targetHandle?: string | null;
    }
>(nodes: N[] | undefined, edges: E[] | undefined): { nodes: N[]; edges: E[] } {
    const safeNodes = Array.isArray(nodes) ? nodes : [];
    const safeEdges = Array.isArray(edges) ? edges : [];
    if (safeNodes.length === 0) return { nodes: safeNodes, edges: safeEdges };

    const idCounts = new Map<string, number>();
    for (const node of safeNodes) {
        idCounts.set(node.id, (idCounts.get(node.id) ?? 0) + 1);
    }
    const needsHeal = safeNodes.some(
        (node) => !node.id || (idCounts.get(node.id) ?? 0) > 1
    );
    if (!needsHeal) return { nodes: safeNodes, edges: safeEdges };

    const RIGHT_OFFSET = 400;
    const BOTTOM_OFFSET = 200;
    const DIRECTIONS = ["right", "bottom"] as const;
    type Direction = (typeof DIRECTIONS)[number];
    const posOf = (node: N): { x: number; y: number } =>
        node.position ?? node.positionAbsolute ?? { x: 0, y: 0 };
    const posKey = (p: { x: number; y: number }): string =>
        `${Math.round(p.x)}:${Math.round(p.y)}`;
    const dirOf = (edge: E): Direction =>
        edge.sourceHandle?.split("_").pop() === "bottom" ? "bottom" : "right";

    const oldToNew = new Map<string, string>();
    const newNodes = safeNodes.map((node) => {
        const newId = generateCleanNanoID();
        if (node.id && (idCounts.get(node.id) ?? 0) === 1) {
            oldToNew.set(node.id, newId);
        }
        return { ...node, id: newId };
    });
    type HealedNode = (typeof newNodes)[number];

    const posToNode = new Map<string, HealedNode>();
    for (const node of newNodes) {
        posToNode.set(posKey(posOf(node)), node);
    }
    const geomChild = (
        node: HealedNode,
        direction: Direction
    ): HealedNode | undefined => {
        const base = posOf(node);
        const expected =
            direction === "bottom"
                ? { x: base.x, y: base.y + BOTTOM_OFFSET }
                : { x: base.x + RIGHT_OFFSET, y: base.y };
        const child = posToNode.get(posKey(expected));
        return child && child.id !== node.id ? child : undefined;
    };

    // Align the stored edge tree (over the old ids) onto the physical layout so
    // every operator stays on its correct connection even when node ids are
    // fully lost and edges aren't stored root-first.
    const oldIdToNode = new Map<string, HealedNode>();
    if (safeEdges.length > 0) {
        const oldChildren = new Map<
            string,
            Partial<Record<Direction, string>>
        >();
        const oldTargets = new Set<string>();
        const oldIds = new Set<string>();
        for (const edge of safeEdges) {
            const entry = oldChildren.get(edge.source) ?? {};
            entry[dirOf(edge)] = edge.target;
            oldChildren.set(edge.source, entry);
            oldTargets.add(edge.target);
            oldIds.add(edge.source);
            oldIds.add(edge.target);
        }
        const oldRoots = [...oldIds].filter((id) => !oldTargets.has(id));

        const geomChildIds = new Set<string>();
        for (const node of newNodes) {
            for (const direction of DIRECTIONS) {
                const child = geomChild(node, direction);
                if (child) geomChildIds.add(child.id);
            }
        }
        const geomRoots = newNodes.filter((n) => !geomChildIds.has(n.id));

        // Only trust the alignment when both trees have a single matching root;
        // otherwise leave it to the per-edge geometry fallback below.
        if (oldRoots.length === 1 && geomRoots.length === 1) {
            const queue: Array<[string, HealedNode]> = [
                [oldRoots[0], geomRoots[0]],
            ];
            while (queue.length) {
                const [oldId, node] = queue.shift()!;
                if (oldIdToNode.has(oldId)) continue; // guard against cycles
                oldIdToNode.set(oldId, node);
                const children = oldChildren.get(oldId);
                if (!children) continue;
                for (const direction of DIRECTIONS) {
                    const childOldId = children[direction];
                    if (!childOldId) continue;
                    const childNode = geomChild(node, direction);
                    if (childNode) queue.push([childOldId, childNode]);
                }
            }
        }
    }

    const usedTargets = new Set<string>();
    const newEdges = safeEdges
        .map((edge) => {
            const direction = dirOf(edge);

            // 1) direct remap (valid old node ids), 2) topology alignment
            let source =
                oldToNew.get(edge.source) ?? oldIdToNode.get(edge.source)?.id;
            let target =
                oldToNew.get(edge.target) ?? oldIdToNode.get(edge.target)?.id;

            // 3) geometry fallback for anything still unresolved
            if (!source || !target || usedTargets.has(target)) {
                source = undefined;
                target = undefined;
                for (const candidate of newNodes) {
                    const child = geomChild(candidate, direction);
                    if (child && !usedTargets.has(child.id)) {
                        source = candidate.id;
                        target = child.id;
                        break;
                    }
                }
            }

            if (!source || !target) return null;
            usedTargets.add(target);

            const sourceHandle = `${source}_${direction}`;
            const targetHandle = `${target}`;
            return {
                ...edge,
                source,
                target,
                sourceHandle,
                targetHandle,
                id: `reactflow__edge-${source}${sourceHandle}-${target}${targetHandle}`,
            };
        })
        .filter((edge): edge is NonNullable<typeof edge> => edge !== null);

    return { nodes: newNodes, edges: newEdges as E[] };
}

/**
 * Generates a NanoID and replaces '-' characters with random digits.
 * @param {number} size - The size of the NanoID to generate.
 * @returns {string} - The generated NanoID with '-' replaced by random digits.
 */
export function extractSourceHandleId(id: string): string {
    const [sourceid] = id.split("_");
    return sourceid;
}

export const extractUsedResponses = (
    nodes:
        | ReactFlowNode<MultipleResponseNode<MultiResponseNodeType>>[]
        | undefined = []
): MultiResponseBlockData<MultiResponseNodeType>["blocks"] => {
    if (
        nodes.length &&
        nodes.find((e) => e.type === BertNodeKeys.createMultiResponseNode)
    )
        return [];
    return nodes
        ?.filter((e) => e?.data?.type === "response")
        ?.map((e) => e.data.metadata.blocks)
        ?.flat();
};

export const highlightSearchQuery = (htmlString: string, query: string) => {
    if (!query) return htmlString;

    const escapeRegExp = (string: string) => {
        return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    };

    const escapedQuery = escapeRegExp(query);

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");

    const walkAndHighlight = (node: Node) => {
        if (node.nodeType === 3) {
            // text node
            const regex = new RegExp(`(${escapedQuery})`, "gi");
            if (node.nodeValue) {
                const tempDiv = document.createElement("div");
                tempDiv.innerHTML = node.nodeValue.replace(regex, "⚑$1⚑");

                while (tempDiv.firstChild) {
                    node.parentNode!.insertBefore(tempDiv.firstChild, node);
                }
                node.parentNode!.removeChild(node);
            }
        } else if (node.nodeType === 1 && node.nodeName !== "SCRIPT") {
            // element node
            Array.from(node.childNodes).forEach(walkAndHighlight);
        }
    };

    walkAndHighlight(doc.body);

    return doc.body.innerHTML.replace(/⚑(.*?)⚑/g, "<mark>$1</mark>");
};

export const isDarkMode = (theme: Theme): boolean => {
    return theme.palette.mode === "dark";
};

type JsonValue =
    | string
    | number
    | boolean
    | null
    | undefined
    | JsonObject
    | JsonArray;
type JsonObject = { [key: string]: JsonValue };
type JsonArray = JsonValue[];

export function jsonToFormData(json: JsonObject): FormData {
    const formData = new FormData();

    function appendToFormData(data: JsonValue, parentKey: string = ""): void {
        if (data === undefined) {
            // Ignore undefined values
            return;
        }

        if (
            data &&
            typeof data === "object" &&
            !(data instanceof Date) &&
            !(data instanceof File)
        ) {
            Object.entries(data).forEach(([key, value]) => {
                const newKey = parentKey ? `${parentKey}[${key}]` : key;
                appendToFormData(value, newKey);
            });
        } else if (Array.isArray(data)) {
            data.forEach((item, index) => {
                const newKey = `${parentKey}[${index}]`;
                appendToFormData(item, newKey);
            });
        } else {
            const value = data === null ? "" : data.toString();
            formData.append(parentKey, value);
        }
    }

    appendToFormData(json);
    return formData;
}

export function dropDownActiveClass(active: boolean) {
    return active ? "active dropdown-item-active" : "";
}

export const errorReload = (error: string, retries = 1) => {
    console.error(error);

    const errRetries: string | null = sessionStorage.getItem("errRetries");
    console.warn("errorReload()", typeof errRetries, errRetries);
    if (typeof errRetries === "string") {
        // we have tried before
        if (parseInt(errRetries) >= retries) {
            // ah, give up
            console.warn("errorReload() now give up");
            return;
        }

        console.warn("errorReload() now try again");
        sessionStorage.setItem("errRetries", String(parseInt(errRetries) + 1));
    } else {
        // we have not tried before
        console.warn("errorReload() try again");
        sessionStorage.setItem("errRetries", "1");
    }

    window.location.reload();
};

export const encryptString = (
    text: string,
    secretKey: string = import.meta.env.VITE_ENCRYPT_SECRET_KEY
): string => {
    try {
        const key = CryptoJS.SHA256(secretKey);

        const iv = CryptoJS.lib.WordArray.random(16);

        const encrypted = CryptoJS.AES.encrypt(text, key, {
            iv: iv,
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7,
        });

        const combined = iv.concat(encrypted.ciphertext);

        const result = CryptoJS.enc.Base64.stringify(combined);
        return result;
    } catch (error) {
        console.error("Encryption failed:", error);
        throw new Error("Encryption failed");
    }
};
const SECRET_KEY = "abcniuxdY2UGG";

const toUrlSafeBase64 = (cipherText: string): string => {
    return cipherText
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/, "");
};

const fromUrlSafeBase64 = (urlSafeCipherText: string): string => {
    let base64 = urlSafeCipherText.replace(/-/g, "+").replace(/_/g, "/");
    const padding = 4 - (base64.length % 4);
    if (padding !== 4) {
        base64 += "=".repeat(padding);
    }
    return base64;
};

export const encrypt = (data: object): string => {
    const jsonString = JSON.stringify(data);

    // Encrypting using AES with default configurations
    const encrypted = CryptoJS.AES.encrypt(jsonString, SECRET_KEY).toString();

    // Convert to URL-safe format
    const urlSafeEncrypted = toUrlSafeBase64(encrypted);

    return urlSafeEncrypted;
};

// Function to Decrypt Data
export const decrypt = <T>(cipherText: string): T => {
    try {
        // Convert from URL-safe format to standard Base64
        const formattedCipherText = fromUrlSafeBase64(cipherText);

        // Decrypt the formatted cipherText
        const bytes = CryptoJS.AES.decrypt(formattedCipherText, SECRET_KEY);

        // Convert bytes to UTF-8 string
        const decryptedData = bytes.toString(CryptoJS.enc.Utf8);

        // Parse and return the decrypted JSON object
        return decryptedData ? (JSON.parse(decryptedData) as T) : ({} as T);
    } catch (error) {
        console.error("Decryption failed:", error);
        return {} as T;
    }
};

export const getResponseLabel = (
    question: Question,
    response: string | number
) => {
    response = String(response);
    if (response === "-1") {
        return "NA";
    }
    if (question.question_type === "rating") {
        return response;
    }

    if (question.question_type === "yes_no") {
        switch (response) {
            case "1":
                return "Yes";
            case "0":
                return "No";
            default:
                return "NA";
        }
    }
    if (question.question_type === "custom") {
        const customResponse = question.settings.custom.find(
            (e) => `${e.id}` === response
        );
        return customResponse?.name ?? "NA";
    }
    return "NA";
};

export const removeCallveriqFromString = (text: string | undefined): string => {
    if (!isDefined(text)) return "";
    return text.replace(/callveriq/gi, "").trim();
};

export const truncateString = (
    text: string | undefined,
    maxLength: number
): string => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + "...";
};

/** Finds the "true"/"yes" eligible value for a boolean custom entity — its
 * keywords are backend-configured, not a fixed app-wide constant. */
export const findPositiveEntityValue = (
    entityList: EntityListResponse[] | undefined,
    entityId: number,
    trueLabel?: string
): string | undefined => {
    const valuesList = entityList?.find(
        (item) => item.id === entityId
    )?.valuesList;
    const normalizedTrueLabel = trueLabel?.trim().toLowerCase();

    if (normalizedTrueLabel) {
        const valueByTrueLabel = valuesList?.find(
            (v) => v.label?.trim().toLowerCase() === normalizedTrueLabel
        )?.id;
        if (valueByTrueLabel) return valueByTrueLabel;
    }

    return (
        valuesList?.find((v) => /^(true)$/i.test(v.id))?.id ??
        valuesList?.find((v) => /^(yes)$/i.test(v.id))?.id ??
        valuesList?.[0]?.id
    );
};

export const ciInsightsOptions = [
    {
        id: "reason",
        label: "Reasons",
    },
    { id: "objection", label: "Objections" },
    { id: "question", label: "Questions" },
    { id: "feature", label: "Product Features" },
    { id: "competition", label: "Competition" },
    { id: "sentiment", label: "Sentiment" },
];

export const modulesOptions: moduleOptionsType[] = [
    { id: "customer_intelligence", label: "Customer Intelligence" },
    { id: "ai_insights", label: "AI Insights" },
    { id: "custom_tracking", label: "Custom Tracking" },
];
export const getDefaultConfigOptions: (n: number) => Array<{
    index: number;
    text: string;
}> = (n) => {
    return [
        {
            index: 0,
            text: "All Blocks",
        },
        ...new Array(n ? n - 1 : 0)
            .fill(0)
            .map((_, index) => ({
                text: `Any ${index + 1} Blocks`,
                index: index + 1,
            }))
            .reverse(),
    ];
};

export const handleVersionChange = () => {
    if ("serviceWorker" in navigator) {
        navigator.serviceWorker
            .getRegistrations()
            .then((registrations) => {
                for (const registration of registrations) {
                    registration.unregister(); // Unregister all service workers
                }
            })
            .then(() => {
                // Clear all caches (optional)
                caches.keys().then((cacheNames) => {
                    return Promise.all(
                        cacheNames.map((cacheName) => {
                            return caches.delete(cacheName);
                        })
                    );
                });
            })
            .then(() => {
                // Force a hard refresh
                window.location.reload();
            });
    } else {
        // If service workers are not supported, just refresh
        window.location.reload();
    }
};

export function deepCloneStructured<T>(obj: T): T {
    return structuredClone(obj);
}

export function getSingularPlural(
    count: number,
    singular: string,
    plural: string
) {
    return count === 1 ? singular : plural;
}

/**
 * Generates start_time and end_time based on the provided parameters
 * @param data - Object containing from_ref, operation, seconds, and optional moment_id
 * @returns Object with start_time and end_time strings, or null if validation fails
 */
export function generateStartEndTime(data: MomentBlockFormType): {
    start_time: string | null;
    end_time: string | null;
} {
    const { from, location, operation, seconds, moment } = data;
    if (location !== "specific") {
        return {
            start_time: "__result__=talk_start_time",
            end_time: "__result__=talk_end_time",
        };
    }

    const result: {
        start_time: string | null;
        end_time: string | null;
    } = {
        start_time: "__result__=talk_start_time",
        end_time: "__result__=talk_end_time",
    };

    if (from === "moment" && isDefined(moment)) {
        const cleanMomentId = moment!.id!.replace("-", "_");

        if (operation === "+") {
            result.start_time = `__result__= moment_${cleanMomentId}_start_time`;
            result.end_time = `__result__= moment_${cleanMomentId}_start_time ${operation} ${seconds}`;
        } else if (operation === "-") {
            result.start_time = `__result__= moment_${cleanMomentId}_start_time ${operation} ${seconds}`;
            result.end_time = `__result__= moment_${cleanMomentId}_start_time`;
        } else if (operation === "+/-") {
            result.start_time = `__result__= moment_${cleanMomentId}_start_time`;
            result.end_time = `__result__= moment_${cleanMomentId}_end_time`;
        }
    } else {
        if (isDefined(from) && from.includes("start_time")) {
            result.end_time = `__result__= ${from} ${operation} ${seconds}`;
            result.start_time = "__result__=talk_start_time";
        } else if (isDefined(from) && from.includes("end_time")) {
            result.start_time = `__result__= ${from} ${operation} ${seconds}`;
            result.end_time = "__result__=talk_end_time";
        }
    }

    return result;
}
export const formatDraftConfigJSON: (
    responses: RuleEngineConfig["draft_config"]
) => RuleEngineConfig["draft_config"] = (responses) => {
    return responses.map((res) => ({
        ...res,
        /* Set the selection flag to false as you don't want the request to be made 
                if the user just clicks on a card and has not done any edditing */
        edges: res.edges.map((edge) => ({
            ...edge,
            selected: false,
        })),
        nodes: res.nodes
            .filter(
                (node) =>
                    node.type !== BertNodeKeys.createMultiResponseNode &&
                    node.type !== BertNodeKeys.createSingleResponseNode
            )
            .map((node) => ({ ...node, selected: false })),
    }));
};
const getDateKey = (filtersDefaultKey: string | null, fallbackKey: string) => {
    if (!isDefined(filtersDefaultKey)) return fallbackKey;
    return Object.values(datekeys).includes(filtersDefaultKey)
        ? filtersDefaultKey
        : fallbackKey;
};

export const generateDefaultFilters = (
    domainConfig: DomainInstance,
    state: GlobalFiltersStateType
) => {
    const initFilters: Partial<
        ConversationSearchDecodedFilters & typeof state
    > = {};
    const filters = domainConfig.getDefaultFilters();
    if (filters.default_level === "account") initFilters.level = Level.account;
    else initFilters.level = Level.conversation;

    if (filters.default_audit_mode === "manual")
        initFilters.auditType = AuditType.manual;

    if (isDefined(filters.conversation_processing_status))
        initFilters.processingStatus = filters.conversation_processing_status;
    else initFilters.processingStatus = null;

    const { durationOptions } = state;
    const durationKey = Object.keys(state.durationOptions).find((key) => {
        const [min, max] = durationOptions[key].value;
        return (
            min === filters.conversation_duration_range?.min &&
            max === filters.conversation_duration_range?.max
        );
    });

    if (isDefined(filters.conversation_duration_range) && !durationKey) {
        initFilters.minDuration =
            filters.conversation_duration_range?.min ?? null;
        initFilters.maxDuration =
            filters.conversation_duration_range?.max ?? null;
        const { name: durationName, value: durationValue } =
            getCustomDurationFilter(
                initFilters.minDuration ?? null,
                initFilters.maxDuration ?? null
            );

        const existingKey =
            Object.keys(durationOptions).find(
                (key) => durationOptions[key].name === durationName
            ) ?? durationName;

        initFilters.durationKey = existingKey;
        if (existingKey === durationName) {
            initFilters.durationOptions = {
                ...state.durationOptions,
                [durationName]: {
                    value: durationValue,
                    name: durationName,
                },
            };
        }
    }

    initFilters.meetingType = filters?.conversation_type ?? "call";
    return {
        ...initFilters,
        durationKey:
            initFilters.meetingType !== "call"
                ? durationkeys.any
                : initFilters.durationKey ?? durationKey ?? state.durationKey,
        dateKey: getDateKey(
            filters?.conversation_default_date,
            GlobalFiltersInitialState.dateKey
        ),
    };
};

export const getSentimentText = (sentiment: number) => {
    return sentiment > 0
        ? "positive"
        : sentiment === 0
        ? "neutral"
        : "negative";
};

// Type definitions for dataMaper function
interface AssessmentResponse {
    question_id: string;
    question: string;
    is_correct: boolean | null;
    answer: string | number;
    options: string[];
    correct_answers: string[];
    is_pie: boolean;
}

interface DataMapperResult {
    [questionId: string]: string[] | { [option: string]: number };
}

export const dataMaper = (
    data: Record<string, AssessmentResponse[]>
): DataMapperResult => {
    // This function processes assessment response data to track answer frequencies
    // For multiple choice questions: Creates a mapping of {questionId: {optionA: count, optionB: count}}
    // For free text questions: Creates an array of responses {questionId: [answer1, answer2, ...]}
    const obj: DataMapperResult = {};
    Object.values(data)
        .flat()
        .forEach((e: AssessmentResponse) => {
            const markedAnswers = e.answer;

            if (obj[e.question_id]) {
                // If we've seen this question before, increment counts for each selected answer
                // Check if it's a free text question by looking at the answer type
                if (typeof markedAnswers === "string") {
                    (obj[e.question_id] as string[]).push(markedAnswers);
                } else {
                    // For multiple choice, we need to handle the answer as a string
                    const answerStr = String(markedAnswers);
                    (obj[e.question_id] as { [option: string]: number })[
                        answerStr
                    ] =
                        ((obj[e.question_id] as { [option: string]: number })[
                            answerStr
                        ] ?? 0) + 1;
                }
            } else {
                // For new questions, initialize counts to 0 for all possible options
                if (typeof markedAnswers === "string") {
                    obj[e.question_id] = [markedAnswers];
                } else {
                    obj[e.question_id] = {};
                    e?.options?.forEach(
                        (val: string) =>
                            ((
                                obj[e.question_id] as {
                                    [option: string]: number;
                                }
                            )[val] = 0)
                    );
                    const answerStr = String(markedAnswers);
                    (obj[e.question_id] as { [option: string]: number })[
                        answerStr
                    ] =
                        ((obj[e.question_id] as { [option: string]: number })[
                            answerStr
                        ] ?? 0) + 1;
                }
            }
        });
    return obj;
};
// Type definitions for graphDataCreater function
import { PieDataItem } from "@callveriq/components/chart/DonutChart";
import { QuestionType } from "@callveriq/type/lms";

interface GraphQuestion {
    question_id: string;
    question: string;
    is_correct: boolean | null;
    answer: string | number;
    options: string[];
    correct_answers: string[];
    question_type: QuestionType;
    is_pie: boolean;
}

interface GraphDataItem {
    isPie: boolean;
    data: PieDataItem[];
    question_text: string;
    total_response: number;
    question_type: QuestionType;
}

interface GraphDataCreaterResult {
    temp_data: PieDataItem[][];
    temp_Graph: GraphDataItem[];
}

export const graphDataCreater = (
    obj: DataMapperResult,
    res: Record<string, GraphQuestion[]>
): GraphDataCreaterResult => {
    const temp_data: PieDataItem[][] = [];
    const temp_Graph: GraphDataItem[] = [];

    Object.keys(obj).map((item: string) => {
        const optionObj = obj[item];
        const temp: PieDataItem[] = [];
        let response_count = 0;
        let total_count = 0;

        Object.keys(optionObj).map((option: string) => {
            total_count +=
                (optionObj as { [option: string]: number })[option] || 0;
        });

        if (Array.isArray(optionObj)) {
            (optionObj as string[]).forEach((option: string) => {
                response_count++;
                temp.push({
                    label: option,
                    count: 1,
                    percentage: 0,
                    color: "",
                    sentiment: null,
                });
            });
        } else {
            Object.keys(optionObj).map((option: string) => {
                const count =
                    (optionObj as { [option: string]: number })[option] || 0;
                response_count += count;
                temp.push({
                    label: option,
                    count: count,
                    percentage: formatFloat((count / total_count) * 100, 2),
                    color: getColorFromString(option),
                    sentiment: null,
                });
            });
        }

        temp_data.push(temp);
        let largestArray: GraphQuestion[] | null = null;

        for (const prop in Object.values(res)) {
            if (Array.isArray(Object.values(res)[prop])) {
                if (
                    !largestArray ||
                    (Object.values(res)[prop] as GraphQuestion[]).length >
                        largestArray.length
                ) {
                    largestArray = Object.values(res)[prop] as GraphQuestion[];
                }
            }
        }

        const ques = largestArray?.find(
            (ques: GraphQuestion) => ques.question_id === item
        );

        temp_Graph.push({
            isPie: ques?.is_pie || false,
            data: temp,
            question_text: ques?.question || "",
            total_response: response_count,
            question_type: ques?.question_type ?? "FREETEXT",
            //is_nine: ques.options.length === 10 ? true : false,
        });
    });

    return { temp_data, temp_Graph };
};
